import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-play-circle-fill text-background-50 text-xl"></i>
            </div>
            <span className={`text-lg font-bold transition-colors duration-300 ${
              scrolled || !isHome ? 'text-foreground-900' : 'text-background-50'
            }`}>
              海角社区视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                location.pathname === '/'
                  ? 'bg-primary-50 text-primary-700'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-50 text-primary-700'
                    : scrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCategoryOpen(false)}></div>
                  <div className="absolute top-full mt-2 left-0 w-48 bg-background-50 rounded-xl shadow-lg border border-background-200 z-20 py-2">
                    {categories.filter(c => c.id !== 'all').map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        onClick={() => setCategoryOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                      >
                        <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
                          <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                        </div>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'bg-primary-50 text-primary-700'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索视频..."
                className="w-48 pl-9 pr-3 py-2 text-sm rounded-full bg-background-100 border border-background-200 text-foreground-700 placeholder-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            </div>
            <button className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              创作中心
            </button>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl ${
              scrolled || !isHome ? 'text-foreground-800' : 'text-background-50'
            }`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 rounded-2xl shadow-lg border border-background-200 mb-3 p-3 animate-in">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors">
              <i className="ri-home-line text-lg"></i>
              <span className="text-sm font-medium">首页</span>
            </Link>
            <div className="border-t border-background-200 my-1"></div>
            <p className="px-3 py-2 text-xs text-foreground-400 font-medium">分类浏览</p>
            {categories.filter(c => c.id !== 'all').map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
                  <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                </div>
                <span className="text-sm">{cat.name}</span>
              </Link>
            ))}
            <div className="border-t border-background-200 my-1"></div>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-foreground-700 hover:bg-background-100 rounded-xl transition-colors">
              <i className="ri-information-line text-lg"></i>
              <span className="text-sm font-medium">关于我们</span>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}